import React, { useState, useEffect, useRef, Component } from 'react';

class DwvErrorBoundary extends Component {
    constructor(props) { super(props); this.state = { hasError: false }; }
    static getDerivedStateFromError() { return { hasError: false }; }
    componentDidCatch(err) {
        if (err && err.message && err.message.includes('Non constant')) return;
        console.warn('DWV error caught:', err?.message);
    }
    render() { return this.props.children; }
}
import * as dwv from 'dwv';
const ORTHANC_PROXY = '/api/pacs/orthanc';
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const ORTHANC = API_BASE + ORTHANC_PROXY;

function authHeaders() {
    const token = localStorage.getItem('hims_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function DicomViewer() {
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [tool, setTool] = useState('WindowLevel');
    const [info, setInfo] = useState(null);
    const appRef = useRef(null);
    const divId = 'dwv-container';

    // Orthanc browser state
    const [tab, setTab] = useState('upload'); // 'upload' | 'orthanc'
    const [orthancStudies, setOrthancStudies] = useState([]);
    const [orthancLoading, setOrthancLoading] = useState(false);
    const [orthancError, setOrthancError] = useState('');
    const [selectedStudy, setSelectedStudy] = useState(null);
    const [series, setSeries] = useState([]);

    useEffect(() => {
        // Suppress DWV's non-fatal "Non constant number of volumes" uncaught error
        const handler = (e) => {
            if (e.message && e.message.includes('Non constant')) {
                e.preventDefault();
                return true;
            }
        };
        window.addEventListener('error', handler);
        return () => {
            window.removeEventListener('error', handler);
            if (appRef.current) {
                try { appRef.current.reset(); } catch {}
            }
        };
    }, []);

    // Load Orthanc studies
    const loadOrthancStudies = async () => {
        setOrthancLoading(true);
        setOrthancError('');
        try {
            const res = await fetch(`${ORTHANC}/studies`, { headers: authHeaders() });
            if (!res.ok) throw new Error('Cannot reach Orthanc via HIMS backend');
            const data = await res.json();
            setOrthancStudies(data);
        } catch (e) {
            setOrthancError('Orthanc not reachable: ' + e.message + '. Make sure Orthanc is running.');
        } finally {
            setOrthancLoading(false);
        }
    };

    useEffect(() => {
        if (tab === 'orthanc') loadOrthancStudies();
    }, [tab]);

    // Load series for a study
    const openStudy = async (study) => {
        setSelectedStudy(study);
        setSeries([]);
        try {
            const res = await fetch(`${ORTHANC}/studies/${study.ID}/series`, { headers: authHeaders() });
            const data = await res.json();
            setSeries(data);
        } catch {}
    };

    // Load series from Orthanc via DICOMweb WADO-RS into DWV
    const loadSeriesFromOrthanc = async (seriesItem) => {
        setLoading(true);
        setError('');
        setInfo(null);
        setFiles([]);
        try {
            if (appRef.current) {
                try { appRef.current.reset(); } catch {}
            }

            // Get all instances in this series
            const res = await fetch(`${ORTHANC}/series/${seriesItem.ID}/instances`, { headers: authHeaders() });
            const instances = await res.json();

            if (!instances.length) throw new Error('No instances found in this series');

            // Fetch each DICOM file as ArrayBuffer (with JWT auth), convert to File objects for DWV
            const dicomFiles = await Promise.all(
                instances.map(async (inst, i) => {
                    const r = await fetch(`${ORTHANC}/instances/${inst.ID}/file`, { headers: authHeaders() });
                    if (!r.ok) throw new Error(`Instance fetch failed: ${r.status}`);
                    const buf = await r.arrayBuffer();
                    return new File([buf], `instance_${i}.dcm`, { type: 'application/dicom' });
                })
            );

            const app = new dwv.App();
            appRef.current = app;

            app.init({
                dataViewConfigs: { '*': [{ divId }] },
                tools: {
                    Scroll: {},
                    WindowLevel: {},
                    ZoomAndPan: {},
                    Draw: { options: ['Ruler'] },
                },
            });

            app.addEventListener('load', () => {
                setLoading(false);
                try {
                    const meta = app.getMetaData(0);
                    if (meta) {
                        setInfo({
                            patient: meta['00100010']?.value?.[0] || selectedStudy?.PatientMainDicomTags?.PatientName || '—',
                            study: meta['00081030']?.value?.[0] || selectedStudy?.MainDicomTags?.StudyDescription || '—',
                            modality: meta['00080060']?.value?.[0] || seriesItem?.MainDicomTags?.Modality || '—',
                            date: meta['00080020']?.value?.[0] || selectedStudy?.MainDicomTags?.StudyDate || '—',
                            slices: instances.length,
                        });
                    }
                } catch {}
                app.setTool('WindowLevel');
            });

            app.addEventListener('error', (e) => {
                const msg = e?.error?.message || e?.message || '';
                console.warn('DICOM event:', msg);
                // These are non-fatal DWV warnings — ignore them, load event will still fire
            });

            app.addEventListener('loadend', () => {
                setLoading(false);
            });

            await app.loadFiles(dicomFiles);
        } catch (err) {
            setLoading(false);
            console.error('loadSeriesFromOrthanc error:', err);
            setError('Load error: ' + (err.message || String(err)));
        }
    };

    const loadFiles = async (selectedFiles) => {
        if (!selectedFiles.length) return;
        setLoading(true);
        setError('');
        setInfo(null);
        try {
            if (appRef.current) {
                try { appRef.current.reset(); } catch {}
            }
            const app = new dwv.App();
            appRef.current = app;
            app.init({
                dataViewConfigs: { '*': [{ divId }] },
                tools: {
                    Scroll: {},
                    WindowLevel: {},
                    ZoomAndPan: {},
                    Draw: { options: ['Ruler'] },
                },
            });
            app.addEventListener('load', () => {
                setLoading(false);
                try {
                    const meta = app.getMetaData(0);
                    if (meta) {
                        setInfo({
                            patient: meta['00100010']?.value?.[0] || '—',
                            study: meta['00081030']?.value?.[0] || '—',
                            modality: meta['00080060']?.value?.[0] || '—',
                            date: meta['00080020']?.value?.[0] || '—',
                            slices: app.getNumberOfLoadedImages?.() || '—',
                        });
                    }
                } catch {}
                app.setTool('WindowLevel');
            });
            app.addEventListener('error', (e) => {
                setLoading(false);
                setError('Failed to load DICOM file.');
                console.error('DICOM load error:', e);
            });
            await app.loadFiles(Array.from(selectedFiles));
        } catch (err) {
            setLoading(false);
            setError('DICOM viewer error: ' + (err.message || 'Unknown error'));
        }
    };

    const setActiveTool = (t) => {
        setTool(t);
        if (appRef.current) {
            try { appRef.current.setTool(t); } catch {}
        }
    };

    const handleReset = () => {
        if (appRef.current) {
            try { appRef.current.resetDisplay(); } catch {}
        }
    };

    const TOOLS = [
        { key: 'WindowLevel', label: 'W/L', title: 'Window / Level' },
        { key: 'ZoomAndPan', label: 'Zoom', title: 'Zoom & Pan' },
        { key: 'Scroll', label: 'Scroll', title: 'Scroll Slices' },
        { key: 'Draw', label: 'Ruler', title: 'Draw Ruler' },
    ];

    const hasImage = files.length > 0 || info;

    return (
        <div style={{ maxWidth: 1200 }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                <div>
                    <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0369a1', margin: 0 }}>DICOM Viewer</h2>
                    <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>View studies from Orthanc PACS or upload local .dcm files</p>
                </div>
            </div>

            <div style={{ display: 'flex', gap: 16 }}>
                {/* Left panel — source selector */}
                <div style={{ width: 320, flexShrink: 0 }}>
                    {/* Tab switcher */}
                    <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: '1px solid #e2e8f0', marginBottom: 12 }}>
                        {[
                            { key: 'orthanc', label: 'Orthanc PACS' },
                            { key: 'upload', label: 'Upload File' },
                        ].map(t => (
                            <button key={t.key} onClick={() => setTab(t.key)}
                                style={{ flex: 1, padding: '9px 0', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: tab === t.key ? 700 : 400, background: tab === t.key ? '#0369a1' : '#f8fafc', color: tab === t.key ? 'white' : '#374151' }}>
                                {t.label}
                            </button>
                        ))}
                    </div>

                    {/* Orthanc browser */}
                    {tab === 'orthanc' && (
                        <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
                            <div style={{ padding: '10px 14px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>
                                    Studies ({orthancStudies.length})
                                </span>
                                <button onClick={loadOrthancStudies}
                                    style={{ padding: '4px 10px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', fontSize: 11 }}>
                                    Refresh
                                </button>
                            </div>

                            {orthancLoading && (
                                <div style={{ padding: 24, textAlign: 'center', color: '#64748b', fontSize: 12 }}>Connecting to Orthanc...</div>
                            )}

                            {orthancError && (
                                <div style={{ padding: 14, fontSize: 12, color: '#dc2626', background: '#fef2f2', borderBottom: '1px solid #fca5a5' }}>
                                    {orthancError}
                                </div>
                            )}

                            {!orthancLoading && !orthancError && orthancStudies.length === 0 && (
                                <div style={{ padding: 24, textAlign: 'center', color: '#64748b', fontSize: 12 }}>
                                    No studies in Orthanc yet.
                                    <br /><br />
                                    Upload DICOM files via Orthanc Explorer:<br />
                                    <a href="http://thepaktech.com:8042/app/explorer.html" target="_blank" rel="noreferrer"
                                        style={{ color: '#0369a1' }}>Orthanc Explorer</a>
                                </div>
                            )}

                            <div style={{ maxHeight: 420, overflowY: 'auto' }}>
                                {orthancStudies.map(study => (
                                    <div key={study.ID}
                                        onClick={() => openStudy(study)}
                                        style={{ padding: '10px 14px', borderBottom: '1px solid #f8fafc', cursor: 'pointer', background: selectedStudy?.ID === study.ID ? '#eff6ff' : 'white' }}
                                        onMouseEnter={e => e.currentTarget.style.background = '#f0f9ff'}
                                        onMouseLeave={e => e.currentTarget.style.background = selectedStudy?.ID === study.ID ? '#eff6ff' : 'white'}>
                                        <div style={{ fontSize: 12, fontWeight: 700, color: '#1e293b' }}>
                                            {study.PatientMainDicomTags?.PatientName || 'Unknown Patient'}
                                        </div>
                                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                                            {study.MainDicomTags?.StudyDescription || 'No description'} &nbsp;|&nbsp; {study.MainDicomTags?.StudyDate || '—'}
                                        </div>
                                        <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 1 }}>
                                            {study.Series?.length || 0} series
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Series list */}
                            {selectedStudy && series.length > 0 && (
                                <div style={{ borderTop: '2px solid #e2e8f0' }}>
                                    <div style={{ padding: '8px 14px', fontSize: 11, fontWeight: 700, color: '#374151', background: '#f8fafc' }}>
                                        SERIES — {selectedStudy.PatientMainDicomTags?.PatientName}
                                    </div>
                                    {series.map(s => (
                                        <div key={s.ID}
                                            onClick={() => loadSeriesFromOrthanc(s)}
                                            style={{ padding: '9px 14px', borderBottom: '1px solid #f8fafc', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                            onMouseEnter={e => e.currentTarget.style.background = '#f0f9ff'}
                                            onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                                            <div>
                                                <div style={{ fontSize: 12, fontWeight: 600, color: '#0369a1' }}>
                                                    {s.MainDicomTags?.Modality || '?'} — {s.MainDicomTags?.SeriesDescription || 'Series'}
                                                </div>
                                                <div style={{ fontSize: 11, color: '#64748b' }}>
                                                    {s.Instances?.length || 0} images
                                                </div>
                                            </div>
                                            <span style={{ fontSize: 11, background: '#0369a1', color: 'white', padding: '3px 8px', borderRadius: 5, fontWeight: 700 }}>
                                                Load
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* File upload */}
                    {tab === 'upload' && (
                        <div style={{ background: 'white', border: '2px dashed #bae6fd', borderRadius: 10, padding: 24, textAlign: 'center' }}>
                            <input type="file" id="dicom-upload" accept=".dcm,application/dicom" multiple
                                style={{ display: 'none' }}
                                onChange={e => { const f = Array.from(e.target.files); setFiles(f); loadFiles(f); }} />
                            <label htmlFor="dicom-upload" style={{ cursor: 'pointer' }}>
                                <div style={{ fontSize: 32, marginBottom: 8 }}>📂</div>
                                <div style={{ fontSize: 13, fontWeight: 700, color: '#0369a1', marginBottom: 4 }}>Click to upload .dcm files</div>
                                <div style={{ fontSize: 11, color: '#64748b' }}>Export from PAXERA → Save as .dcm → Upload here</div>
                            </label>
                            {files.length > 0 && (
                                <div style={{ marginTop: 10, fontSize: 11, color: '#0369a1', fontWeight: 600 }}>
                                    {files.length} file{files.length > 1 ? 's' : ''} loaded
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Right panel — viewer */}
                <div style={{ flex: 1 }}>
                    {error && (
                        <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', marginBottom: 12, color: '#dc2626', fontSize: 13 }}>
                            {error}
                        </div>
                    )}

                    {info && (
                        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 16px', marginBottom: 10, display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                            {[
                                { label: 'Patient', value: info.patient },
                                { label: 'Study', value: info.study },
                                { label: 'Modality', value: info.modality },
                                { label: 'Date', value: info.date },
                                { label: 'Images', value: info.slices },
                            ].map(item => (
                                <div key={item.label}>
                                    <div style={{ fontSize: 10, fontWeight: 700, color: '#0369a1', textTransform: 'uppercase' }}>{item.label}</div>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{item.value}</div>
                                </div>
                            ))}
                        </div>
                    )}

                    {hasImage && !loading && !error && (
                        <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center' }}>
                            {TOOLS.map(t => (
                                <button key={t.key} onClick={() => setActiveTool(t.key)} title={t.title}
                                    style={{ padding: '6px 14px', borderRadius: 7, border: `2px solid ${tool === t.key ? '#0369a1' : '#e2e8f0'}`, cursor: 'pointer', fontSize: 12, fontWeight: tool === t.key ? 700 : 400, background: tool === t.key ? '#eff6ff' : 'white', color: tool === t.key ? '#0369a1' : '#374151' }}>
                                    {t.label}
                                </button>
                            ))}
                            <button onClick={handleReset}
                                style={{ padding: '6px 12px', borderRadius: 7, border: '1px solid #e2e8f0', cursor: 'pointer', fontSize: 12, background: '#f1f5f9', color: '#374151', marginLeft: 8 }}>
                                Reset View
                            </button>
                        </div>
                    )}

                    <div style={{ background: '#000', borderRadius: 10, overflow: 'hidden', minHeight: 540, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {loading && (
                            <div style={{ position: 'absolute', zIndex: 10, color: 'white', textAlign: 'center' }}>
                                <div style={{ fontSize: 36, marginBottom: 12 }}>⏳</div>
                                <div style={{ fontSize: 14, fontWeight: 700 }}>Loading DICOM...</div>
                            </div>
                        )}
                        {!hasImage && !loading && (
                            <div style={{ color: '#475569', textAlign: 'center' }}>
                                <div style={{ fontSize: 48, marginBottom: 12 }}>🖥️</div>
                                <div style={{ fontSize: 14 }}>Select a series from Orthanc or upload a .dcm file</div>
                            </div>
                        )}
                        <DwvErrorBoundary>
                    <div id={divId} style={{ width: '100%', height: 540 }} />
                </DwvErrorBoundary>
                    </div>

                    <div style={{ marginTop: 8, fontSize: 11, color: '#94a3b8', textAlign: 'center' }}>
                        W/L: drag up/down &nbsp;|&nbsp; Zoom: scroll wheel &nbsp;|&nbsp; Pan: right-click drag &nbsp;|&nbsp; Slices: Scroll tool + drag
                    </div>
                </div>
            </div>
        </div>
    );
}
